// Material UI
import { styled, Box } from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { Alert } from '@mui/material';

// Components
import RegistrationField from 'components/comp/CompRegistrationField';
import { FieldTypes, fieldValidator } from 'components/comp/CompRegistrationUtils';
import { ScrollRestoreContainer } from 'components/ui/layout';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';

// Utility
import { useNotification } from 'context/notification';

export default function CompRegistration(props) {
    const { form, mode, sessionExpired, onFormChange, onSubmit, onCancel } = props;

    const { notify } = useNotification();

    const handleAttendeeUpdate = (value) => {
        const attendees = form.map((a) => {
            if (a.tempId === value.tempId) {
                return value;
            } else {
                return a;
            }
        });

        onFormChange(attendees);
    };

    // ***** Validate Form Values ***** //

    const handleValidation = () => {
        let formError = false;

        const newAttendeeList = [];

        // Map over every attendee form
        for (const attendee of form) {
            let requiredForm = [];
            let customForm = [];

            // Map over every field, and check for errors
            // Assemble a new form array containing any new error messages
            for (const field of attendee.requiredForm) {
                const { error, errorMsg } = fieldValidator(field.value, field.type, field.required);

                if (error) {
                    formError = true;
                    requiredForm.push({ ...field, errorMsg });
                } else {
                    requiredForm.push(field);
                }
            }
            for (const field of attendee.customForm) {
                const { error, errorMsg } = fieldValidator(field.value, field.type, field.required);

                if (error) {
                    formError = true;
                    customForm.push({ ...field, errorMsg });
                } else {
                    customForm.push(field);
                }
            }

            newAttendeeList.push({ ...attendee, requiredForm, customForm });
        }

        if (formError === false) {
            onSubmit();
        } else {
            onFormChange(newAttendeeList);
            notify.warning('Please ensure the form is filled in correctly');
        }
    };

    return (
        <ScrollRestoreContainer>
            <FormContainer>
                <Alert severity="info" variant="standard">
                    Here are the checkout questions that would be shown to customers making this
                    order. You are not required to fill in this information if you don't want or
                    need to.
                </Alert>

                {form.map((attendee, index) => (
                    <AttendeeForm
                        key={index}
                        attendee={attendee}
                        mode={mode}
                        onAttendeeUpdate={handleAttendeeUpdate}
                    />
                ))}

                <Box display="flex" justifyContent="flex-end" mt={4}>
                    <CancelButton onClick={onCancel}>Back</CancelButton>
                    <SubmitButton onClick={handleValidation} disabled={sessionExpired}>Next</SubmitButton>
                </Box>
            </FormContainer>
        </ScrollRestoreContainer>
    );
}

function AttendeeForm(props) {
    const { attendee, mode, onAttendeeUpdate } = props;

    const requiredForm = attendee.requiredForm;
    const customForm = attendee.customForm;

    // ***** Process Form Value Changes ***** //

    const handleRequiredFormChange = (index, value, optionId) => {
        const newForm = Array.from(requiredForm);

        switch (requiredForm[index].type) {
            case FieldTypes.AGE:
                newForm[index].value = value.replace(/[^0-9]/g, '').slice(0, 3);
                break;

            case FieldTypes.MULTIPLE_CHOICE:
                value === true
                    ? newForm[index].value.add(optionId)
                    : newForm[index].value.delete(optionId);
                break;

            default:
                newForm[index].value = value;
                break;
        }

        newForm[index].errorMsg = null;

        onAttendeeUpdate({ ...attendee, requiredForm: newForm });
    };

    const handleCustomFormChange = (index, value, optionId) => {
        const newForm = Array.from(customForm);

        switch (customForm[index].type) {
            case FieldTypes.AGE:
                newForm[index].value = value.replace(/[^0-9]/g, '').slice(0, 3);
                break;

            case FieldTypes.MULTIPLE_CHOICE:
                value === true
                    ? newForm[index].value.add(optionId)
                    : newForm[index].value.delete(optionId);
                break;

            default:
                newForm[index].value = value;
                break;
        }

        newForm[index].errorMsg = null;

        onAttendeeUpdate({ ...attendee, customForm: newForm });
    };

    return (
        <AttendeeFormContainer>
            {mode === 2 && (
                <CardHeader
                    title={attendee.productName}
                    subheader={'Attendee #' + attendee.tempId}
                />
            )}
            <CardContent>
                {requiredForm.map((field, index) => (
                    <RegistrationField
                        key={'1-' + index}
                        index={index}
                        onChange={handleRequiredFormChange}
                        {...field}
                    />
                ))}
                {customForm.map((field, index) => (
                    <RegistrationField
                        key={'2-' + index}
                        index={index}
                        onChange={handleCustomFormChange}
                        {...field}
                    />
                ))}
            </CardContent>
        </AttendeeFormContainer>
    );
}

const FormContainer = styled(Box)(({ theme }) => ({
    width: theme.breakpoints.values.md,
    paddingBottom: theme.spacing(10),
}));

const AttendeeFormContainer = styled(Card)(({ theme }) => ({
    marginTop: theme.spacing(4),
}));